import c from 'classnames';
import { useLocation, useNavigate } from 'react-router-dom';

import { useAnalytics } from 'contexts/analytics/AnalyticsProvider';
import {
  ClickableEventNames,
  EventPropertiesMap,
} from 'contexts/analytics/types';
import { useMobileDetection } from 'hooks/useMobileDetection';

import {
  PaletteColor,
  TextColor,
  Typography,
  TypographyComponentVariant,
} from '@repo/ui';
import {
  ButtonColor,
  ButtonColors,
  ButtonSize,
  ButtonSizes,
  ButtonVariants,
  Card,
  DesignButton,
} from 'components';

import styles from './RecommendedCard.module.css';

export type RecommendedCardProps<
  E extends ClickableEventNames = 'Clicked Button',
> = {
  title: React.ReactNode;
  label: React.ReactNode;
  imgStyles: string;
  cardColor: string;
  buttonColor?: ButtonColor | PaletteColor;
  buttonSize?: ButtonSize;
  buttonSizeMobile?: ButtonSize;
  titleVariant?: TypographyComponentVariant;
  titleVariantMobile?: TypographyComponentVariant;
  titleColor?: string;
  labelVariant?: TypographyComponentVariant;
  labelVariantMobile?: TypographyComponentVariant;
  leftColumnStyles?: string;
  cardsLength?: number;
  analyticsEventName?: E;
  index?: number;
  clickable: EventPropertiesMap[E];
  noOverlay?: boolean;
  onClick?: () => void;
  className?: string;
  titleContainerClassName?: string;
};

export const RecommendedCard = <
  E extends ClickableEventNames = 'Clicked Button',
>({
  title,
  label,
  imgStyles,
  cardColor,
  buttonColor = ButtonColors.PURPLE,
  buttonSize = ButtonSizes.S,
  buttonSizeMobile = ButtonSizes.M,
  titleVariant = 'heading-m',
  titleVariantMobile = 'heading-l',
  titleColor,
  labelVariant = 'label',
  labelVariantMobile = 'body-s',
  leftColumnStyles,
  cardsLength = 0,
  index = 0,
  clickable,
  analyticsEventName = 'Clicked Button' as E,
  onClick,
  className,
  titleContainerClassName,
}: RecommendedCardProps<E>) => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { trackEvent } = useAnalytics();
  const { isMobile } = useMobileDetection();
  const resolvedButtonSize =
    isMobile && buttonSizeMobile ? buttonSizeMobile : buttonSize;
  const resolvedTitleVariant =
    isMobile && titleVariantMobile ? titleVariantMobile : titleVariant;
  const resolvedLabelVariant =
    isMobile && labelVariantMobile ? labelVariantMobile : labelVariant;

  const handleButtonClick = () => {
    onClick?.();
    if (!clickable) return;
    trackEvent(analyticsEventName, clickable);
    if (clickable.href) {
      if (clickable.isExternal) {
        window.open(clickable.href, '_blank');
      } else {
        navigate(clickable.href, { state: { from: pathname } });
      }
    }
  };

  return (
    <Card<E>
      variant='wrapper'
      className={c(
        styles.card,
        cardsLength > 3 && index > 2 && 'hide-on-desktop',
        cardColor,
        className,
      )}
    >
      <div className={c(styles.imageLayer, imgStyles)} aria-hidden />
      <div className={styles.cardLayout}>
        <div className={c(styles.leftColumn, leftColumnStyles)}>
          <div className={c(styles.titleContainer, titleContainerClassName)}>
            <Typography
              variant={resolvedTitleVariant}
              className={styles.title}
              style={titleColor ? { color: titleColor } : undefined}
            >
              {title}
            </Typography>
            <Typography
              variant={resolvedLabelVariant}
              className={styles.label}
              color={TextColor.GREY}
            >
              {label}
            </Typography>
          </div>
          <DesignButton
            label={clickable?.text ?? 'Open'}
            onClick={handleButtonClick}
            variant={ButtonVariants.SOLID}
            size={resolvedButtonSize}
            color={buttonColor}
          />
        </div>
      </div>
    </Card>
  );
};
